// mathOps — value coercion + arithmetic for the blueprint Math Operator nodes
// (+ - * /). Sibling of compareValues.js: pure, no imports, unit-tested
// (tests/unit/mathOps.test.mjs). The operator IS the node type; _resolveDataPin
// dispatches on MATH_OPS and calls computeMath. See src/lib/EventRuntime.js.
//
// Pins carry number (int or float) / string / bool / null (unwired). There is no
// runtime int type — every value is a JS double — so "int mode" is a per-node
// ROUNDING choice (data.resultType === 'int' → Math.trunc, toward zero, C-style),
// not a separate numeric type.
//
// Coercion rule (toNumber): a number passes through (non-finite → 0); a numeric
// string parses; a bool is 1/0; ANYTHING ELSE (null, empty/non-numeric string,
// object) is 0. Unlike the comparison ops there is no string fallback — an empty
// B box on a Subtract is a no-op (a - 0), by design. Divide-by-zero returns 0 and
// is flagged (note '÷0') so a NaN/Infinity never escapes downstream into a Set
// Variable, an array index, or a Delay duration.

export const MATH_OPS = new Set(['add', 'subtract', 'multiply', 'divide'])

const MATH_SYMBOL = { add: '+', subtract: '−', multiply: '×', divide: '÷' }

/**
 * Coerce any pin value to a finite number for arithmetic. Number → itself
 * (NaN/Infinity → 0); numeric string → parsed; bool → 1/0; everything else
 * (null/undefined/empty/non-numeric string/object) → 0.
 * @param {number|string|boolean|null|undefined} v
 * @returns {number}
 */
export function toNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0
  if (typeof v === 'boolean') return v ? 1 : 0
  if (typeof v === 'string') {
    const s = v.trim()
    if (s === '') return 0
    const n = Number(s)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

/**
 * Evaluate a math operator. Both operands are coerced with toNumber. In int mode
 * the result is truncated toward zero. Divide-by-zero yields { value: 0, note:
 * '÷0' } rather than Infinity/NaN. On success note is null.
 * @param {'add'|'subtract'|'multiply'|'divide'} op
 * @param {*} aRaw
 * @param {*} bRaw
 * @param {boolean} [intMode=false]
 * @returns {{ value: number, note: string|null }}
 */
export function computeMath(op, aRaw, bRaw, intMode = false) {
  const a = toNumber(aRaw)
  const b = toNumber(bRaw)
  let value
  let note = null
  switch (op) {
    case 'add':      value = a + b; break
    case 'subtract': value = a - b; break
    case 'multiply': value = a * b; break
    case 'divide':
      if (b === 0) { value = 0; note = '÷0' }
      else         { value = a / b }
      break
    default:         value = 0; note = '?'
  }
  if (intMode) value = Math.trunc(value)
  return { value, note }
}

/** Display glyph for a math operator type (used by the node UI). */
export function mathSymbol(op) {
  return MATH_SYMBOL[op] ?? '?'
}
